import styled from '@emotion/styled'
import { useContext } from 'react'
import {
    CustomizationContext,
    PRIMARY_FONT_CSS_VAR,
} from '../../context/CustomizationContextProvider'
import {
    ApiBrandBenefit,
    ApiLandingPageBannerBenefit,
} from '@storefronts/common-types'
import { useBenefitsTexts } from '../../hooks/useBenefitsTexts'
import { useRotatingIndex } from '../../hooks/useRotatingIndex'
import { getTypographyRoleStyle } from '../../style/typography'
import { CountdownText } from './CountdownText'
import { scaledFontSize } from '../../style/scrollingLayout'

export const BannerContainer = styled.div<{
    backgroundColor: string
    textColor: string
}>(
    {
        display: 'grid',
        gridTemplateAreas: '"content"',
        justifyItems: 'center',
        alignItems: 'center',
        padding: 10,
        // Banner copy: slightly smaller but strong weight for emphasis
        ...getTypographyRoleStyle('promoText', {
            fontFamily: `var(${PRIMARY_FONT_CSS_VAR})`,
            fontWeight: 700,
            lineHeight: 1.3,
        }),
        fontSize: scaledFontSize(14),
        zIndex: 1000,
        width: '100%',
        maxWidth: '100vw',
        textAlign: 'center',
        boxSizing: 'border-box',
        minHeight: 40,
        overflow: 'hidden',
    },
    (props) => ({
        background: props.backgroundColor,
        color: props.textColor,
    }),
)

const BANNER_ROTATION_INTERVAL_MS = 3500

const TextContainer = styled.div<{
    isActive: boolean
    delay: number
}>`
    grid-area: content;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    width: 100%;
    opacity: ${(props) => (props.isActive ? 1 : 0)};
    transition: opacity 0.6s ease-in-out;
    transition-delay: ${(props) => props.delay}s;
`

interface BannerProps {
    bannerText?: string
    bannerBenefits?: ApiLandingPageBannerBenefit[]
    brandBenefits?: ApiBrandBenefit[]
}

export const Banner: React.FC<BannerProps> = ({
    bannerText,
    bannerBenefits = [],
    brandBenefits = [],
}) => {
    const { accentFillColor, accentTextColor } =
        useContext(CustomizationContext)

    const texts = useBenefitsTexts(bannerText, bannerBenefits, brandBenefits)

    const currentIndex = useRotatingIndex(
        texts.length,
        texts.join('\n'),
        BANNER_ROTATION_INTERVAL_MS,
    )

    if (texts.length === 0) return null

    return (
        <BannerContainer
            backgroundColor={accentFillColor}
            textColor={accentTextColor}
        >
            {/* All text containers occupy the same grid area, height auto-adjusts */}
            {texts.map((text, index) => (
                <TextContainer
                    key={`text-${index}`}
                    isActive={index === currentIndex}
                    // Add a slight delay for the incoming text to create a smooth crossfade effect
                    delay={index === currentIndex ? 0.3 : 0}
                >
                    <CountdownText text={text} />
                </TextContainer>
            ))}
        </BannerContainer>
    )
}
